<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <div
            v-if="initialLoading"
            key="initialLoading"
            class="animate__animated animate__fadeIn animate_fadeOut animate__fast"
        >
            <busy-spinner inner-class="my-5" text="Loading Contacts..."></busy-spinner>
        </div>

        <div 
            v-if="!initialLoading"
            key="initialLoadingDone"
        >

            <div class="d-flex flex-wrap justify-content-between align-items-end mb-3 animate__animated animate__fast animate__fadeInUp">
                <base-search-box
                    :value="currentFilterValues.search"
                    ref="searchBox"
                    placeholder="Quick Search..."
                    @search="setFilterAndSearch('search', $event)"
                    @reset="setFilterAndSearch('search', '')"
                ></base-search-box>
                <b-form inline class="mt-2">
                    <div class="align-self-end">
                        <b-button 
                            variant="soft-primary" 
                            :disabled="!showFilterReset" 
                            class="text-uppercase" 
                            @click="clearAllFilters"
                        >
                            Clear Filters
                        </b-button>
                    </div>
                    <base-filter-select
                        label="Portal Access"
                        v-if="isFilterEnabled('portal_access')"
                        v-model="currentFilterValues.portal_access"
                        :reset-value="initialFilterValues.portal_access"
                        :options="portalAccessFilterOptions"
                        @change="getNewList"
                    ></base-filter-select>
                    <b-button
                        v-b-tooltip.hover title="Refresh List"
                        variant="light"
                        class="ml-2 align-self-end"
                        @click="refreshList(true)"
                    >
                        <b-icon icon="arrow-repeat"></b-icon>
                    </b-button>
                </b-form>
                <!-- <div v-if="!loading && totalRows" class="font-size-90 text-muted">{{totalRows}} Vendors</div> -->
                <!-- <b-button variant="success" @click="handleAdd">
                    <b-icon icon="plus-circle" class="mr-1"></b-icon>
                    Add Vendor
                </b-button> -->
            </div>

            <b-card 
                id="vendor-contact-table-card"
                class="shadow rounded-lg animate__animated animate__fadeIn animate__fast"
                no-body
            >
                <div
                    v-if="!loading && !totalRows"
                    class="d-flex justify-content-center align-items-center"
                    style="min-height: 300px;"
                >
                    <div class="text-center">
                        <div class="font-size-100 text-muted mb-4">
                            {{ showFilterReset ? 'No contacts found.  Try a different search' : 'No contacts yet.  Add one?' }}
                        </div>
                        <b-button v-if="showFilterReset" variant="soft-primary" @click="clearAllFilters">
                            Clear Search
                        </b-button>
                        <b-button v-if="!showFilterReset" size="lg" variant="soft-success" @click="handleAdd">
                            Add Contact
                        </b-button>
                    </div>
                </div>

                <b-table 
                    v-else 
                    :items="itemList"
                    :fields="fieldsToShow"
                    :busy="loading"
                    primary-key="id" 
                    no-local-sorting
                    no-sort-reset
                    :sort-by.sync="sortByField"
                    :sort-desc.sync="sortDesc"
                    @sort-changed="getNewList"
                    hover
                    class="no-top-border td-align-middle mb-0"
                    thead-class="overflow-hidden cells-align-middle"
                    head-variant="light"
                >
                    <!-- <template v-slot:table-busy>
                        <div class="text-center text-primary my-2">
                            <busy-spinner text="Loading..."></busy-spinner>
                        </div>
                    </template> -->
                    <template v-slot:cell(full_name)="data">
                        <b-link @click="handleView(data.item.id)">{{ data.value }}</b-link>
                        <base-popover-icon 
                            v-if="data.item.manual_block" 
                            icon="exclamation-circle-fill" 
                            icon-variant="danger" 
                            title="Account On Hold"
                            class="ml-2"
                        >
                            This user's account has been placed on hold by Supplios.  Please contact Supplios support to remove this restriction.
                        </base-popover-icon>
                        <base-popover-icon 
                            v-if="data.item.email_issues_since" 
                            icon="envelope-fill" 
                            icon-variant="danger" 
                            title="Email Issues"
                            class="ml-2"
                        >
                            There are delivery issues with this user's email address.  Please contact Supplios support for more information.
                        </base-popover-icon>
                        <b-badge 
                            v-if="data.item.is_external_admin" 
                            variant="soft-secondary" 
                            class="text-uppercase ml-2"
                            v-b-tooltip.hover 
                            title="This user is an Admin for their company"
                        >Admin</b-badge>
                    </template>
                    <template #cell(company_name)="data">
                        <a v-if="data.item.company_url" :href="data.item.company_url">{{ data.value }}</a>
                        <span v-else>{{ data.value }}</span>
                    </template>
                    <template #cell(tenant_roles)="data">
                        <div class="d-flex gap-2 flex-wrap">
                            <b-badge 
                                v-for="role in (data.value || [])"
                                :key="role.slug"
                                variant="soft-secondary" 
                                class="text-uppercase"
                            >
                                {{ role.name }}
                            </b-badge>
                        </div>
                    </template>
                    <template #cell(portal_access)="data">
                        <b-badge
                            v-if="data.item.portalAccessParams"
                            :key="data.item.portalAccessParams.key"
                            :variant="data.item.portalAccessParams.variant"
                            class="text-uppercase"
                        >
                            {{ data.item.portalAccessParams.label }}
                        </b-badge>
                        <!-- <b-icon v-if="data.item.can_be_invited" icon="circle-fill" variant="success"></b-icon> -->
                    </template>
                    <template #cell(created)="data">
                        {{ data.value | dateFilter }}
                    </template>
                    <template #cell(actions)="data">
                        <b-button-group size="sm">
                            <b-button variant="gray-info" @click="handleView(data.item.id)">
                                View/Edit
                            </b-button>
                            <b-dropdown variant="gray-secondary" no-caret size="sm">
                                <template #button-content>
                                    <b-icon icon="three-dots"></b-icon>
                                </template>
                                <b-dropdown-item
                                    v-if="data.item.email"
                                    key="dropdownHasEmail"
                                    :href="'mailto:' + data.item.email"
                                >
                                    <b-icon icon="envelope" class="mr-2"></b-icon>
                                    Email Contact
                                </b-dropdown-item>
                                <b-dropdown-text
                                    v-else
                                    key="dropdownNoEmail"
                                >
                                    No email address
                                </b-dropdown-text>
                                <b-dropdown-item
                                    v-if="$currentUserInfo.is_paid_seat && data.item.portal_access === 'none' && data.item.can_be_invited"
                                    @click="inviteToPortal(data.item.id)"
                                >
                                    <b-icon icon="door-open" variant="info" class="mr-2"></b-icon>
                                    Invite to Portal
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="$currentUserInfo.is_paid_seat && data.item.portal_access === 'invite_ready' && data.item.can_be_invited"
                                    @click="inviteToPortal(data.item.id)"
                                >
                                    <b-icon icon="door-open" variant="info" class="mr-2"></b-icon>
                                    Send Invite
                                </b-dropdown-item>
                                <b-dropdown-item v-if="$currentUserInfo.is_tenant_admin" :disabled="data.item.portal_access === 'on_hold'" @click="startDeactivate(data.item.id)">
                                    <b-icon icon="trash" variant="danger" class="mr-2"></b-icon>
                                    Deactivate Contact
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-button-group>
                    </template>
                </b-table>
                <template #footer v-if="!loading && totalRows">
                    <base-table-pagination
                        :current-page.sync="currentPage"
                        :per-page.sync="perPage"
                        :total-items="totalRows"
                        show-per-page
                        item-name="contacts"
                        @update:perPage="getNewList"
                        @update:currentPage="getList"
                    ></base-table-pagination>
                </template>
            </b-card>

        </div>  <!-- End of area to hide when loading -->

        <contact-create-update-modal
            ref="contactModal"
            :company-data="companyData"
            :redirect-after-create="!isFixedCompany"
            :hide-save-redirect="isFixedCompany"
            @update-complete="getList"
            @create-complete="getList"
        ></contact-create-update-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import BaseTablePagination from './BaseTablePagination';
import { FormatDateHelper } from '../js/filters-helpers';
import BaseSearchBox from './BaseSearchBox';
import BaseFilterSelect from './BaseFilterSelect';
import ContactCreateUpdateModal from './ContactCreateUpdateModal';  // using this only for updates, not creates
import isEqual from 'lodash/isEqual';
import { getPortalAccessParams } from '../components/tenant_users/tenantUserUtils.js';
import { splTableDefaults, currentUserInfoMixin } from '../js/custom-vue-mixins';

export default {
    name: 'ContactList',
    components: {
        BaseTablePagination,
        BaseSearchBox,
        BaseFilterSelect,
        ContactCreateUpdateModal,
    },
    mixins: [currentUserInfoMixin],
    props: {
        companyId: {  // if you pass in the company ID, it will only return contacts for that company
            // this allows you to re-use this component for Company Contact List also!
            type: [String, Number],
            required: false,
        },
        companyName: {
            // if you are showing it for a specific company, pass the name in here
            // so if you pass in companyId, pass in companyName too
            // because we currently need at least { id, name } to pass to create modal when hard-coding the company
            type: String,
            required: false,
        },
        hideFields: {  // array of strings with field keys to HIDE (if they were to be shown normally)
            type: Array,
            required: false,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            initialLoading: true,  // set to false after getting list the first time
            loading: false,  // loading signal for fetching list
            loadingInvite: false,  // busy flag for sending invite to a contact
            currentPage: 1,  // will get sync'd by base-table-pagination
            perPage: 25,  // will get sync'd by base-table-pagination
            totalRows: null,  // total items in list... need to set it after we get items from API, and also after filtering is applied with @filtered event
            itemList: [],   // list from API
            fields: [
                {
                    key: 'full_name',
                    label: 'Name',
                    sortable: true,
                    api_ordering_name: 'lname',  // override this in our custom method to set fname, lname 
                    tdClass: 'font-weight-bold'
                },{
                    key: 'company_name',
                    label: 'Company',
                    sortable: true,
                    api_ordering_name: 'company__name',
                },{
                    key: 'job_title',
                    label: 'Title',
                },{
                    key: 'work_location',
                    label: 'Work Location',
                },{
                    // the TenantRoles the TU holds: [{id, slug, name, description}] - badges show the name (Oct 2026, was default_contact_for)
                    key: 'tenant_roles',
                    label: 'Contact Type',
                },{
                    key: 'portal_access',
                    label: 'Portal Access?',
                    class: "text-center"
                },{
                    key: 'created',
                    label: 'Date Added',
                    sortable: true,
                    api_ordering_name: 'created',
                    class: 'text-center',
                },{
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center'
                }
            ],
            // filtering
            currentFilterValues: {},  // v-models will update this, set it from initialFilterValues in created hook
            initialFilterValues: {
                search: '',
                portal_access: 'active',  // should be true, false, null -- if null, it won't send query param, and will get all
            },
            allPortalAccessFilterOptions: [  // we filter these in computed depending on user role
                {value: 'active', text: 'Active'},
                //{value: 'all', text: 'All'},  // maybe should not show this... confusing for now at least, on supplier side 
                {value: 'full', text: 'Full Access'},
                {value: 'open_invite', text: 'Open Invitations'},
                {value: 'expired_invite', text: 'Expired Invitations'},
                //{value: 'invite_ready', text: 'Invite Ready'},  // hide on external side for now... until you sort-out how new roles work
                {value: 'on_hold', text: 'On Hold / Deactivated'},
            ],
            hasAppliedFilters: false,  // toggle this on when you are showing filtered results so you know the difference between no results at all, and no results from search
            // for sorting:
            sortByField: 'created',  // sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
            sortDesc: true,  // sort descending = true (adds '-' sign in backend), sort ascending = false (no minus sign)
        }
    },
    computed: {
        showFilterReset: function() {
            // returns BOOLEAN if any currentFilterValues are different than initialFilterValues
            let isSame = isEqual(this.currentFilterValues, this.initialFilterValues);
            return !isSame;  // show filter reset if current filter values are NOT SAME as initial filter values
        },
        fieldsToShow: function() {
            // hide any fields specified in hideFields prop
            if (!this.hideFields || !this.hideFields.length) return this.fields;  // if we didn't specify hiding any fields, just return all of them
            return this.fields.filter(field => !this.hideFields.includes(field.key));  // otherwise only show ones not in hideFields
        },
        isFixedCompany() {
            // is this for a specific company?  we'll just check if we have this.companyId
            return !!this.companyId;
        },
        companyData() {
            // IF this is showing contacts for a specific company, we need to pass companyData { id, name } to the create modal
            // so we'll make sure we have both of those props, then combine them into one object
            // if we do not, we'll use empty object... that should be OK for the create modal to know its not a preset company
            const companyData = {};
            if (this.companyId && this.companyName) {
                companyData['id'] = this.companyId;
                companyData['name'] = this.companyName;
            }
            return companyData;
        },
        portalAccessFilterOptions() {
            // hide the 'deactivated' option unless TU is tenant admin / superadmin
            return this.$currentUserInfo.is_tenant_admin ? [...this.allPortalAccessFilterOptions] : this.allPortalAccessFilterOptions.filter(opt => (opt.value !== 'on_hold'));
        }
    },
    filters: {
        dateFilter: function(value) {
            return FormatDateHelper(value);
        },
    },
    created: function() {
        this.resetFilterValues();
        this.perPage = splTableDefaults.perPage;
    },
    mounted: function () {
        this.getList();
    },
    methods: {
        // PUBLIC METHOD YOU CAN CALL FROM PARENT TO REFRESH LIST, likely just as $refs.something.refreshList();
        refreshList: function(noFilterReset = false) {
            if (noFilterReset) {
                this.getNewList();  // if you specifically set noFilterReset to true (first positional arg), then don't reset filters (but do reset current page to 1)
            } else {
                this.clearAllFilters();   // this resets all filter values and reloads the list
            }
        },
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        // methods to create/update companies using child CompanyCreateUpdateModal (removed from this component)
        handleUpdate: function(updateItemId) {
            // call this only if you want to go DIRECTLY to edit mode, not view mode first
            this.$refs.contactModal.startUpdate(updateItemId);
        },
        handleView: function(itemId) {
            // added because our handleUpdate was really just showing the detail modal... changing it so handleUpdate goes straight to edit mode
            this.$refs.contactModal.startView(itemId);
        },
        handleAdd: function() { // only used if list is empty and user clicks on 'add one' button
            this.$refs.contactModal.startCreate();
        },
        resetFilterValues: function() {
            this.currentFilterValues = Object.assign({}, this.initialFilterValues);
            // new way
            const filterRefs = ['searchBox'];
            filterRefs.forEach(refName => {
                if (this.$refs[refName]) {
                    this.$refs[refName].reset();
                }
            });
        },
        setFilterAndSearch(filterKey, value) {
            // pass in which filter to set and the value - started using this with search box fisrt, but implement for others too
            console.log('filterKey and value', filterKey, value);
            this.currentFilterValues[filterKey] = value;
            this.getNewList();
        },
        clearAllFilters: function() {
            // clear/reset all filters and search
            this.resetFilterValues();
            this.getNewList();
        },
        getOrderingFieldString: function() {
            // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
            // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
            // also add '-' if sortDesc is true
            // FIRST, HANDLE SPECIAL CASE OF ORDERING BY 'full_name'
            if (this.sortByField === 'full_name') {
                if (this.sortDesc) {
                    return '-lname,-fname';
                } else {
                    return 'lname,fname';
                }
            }
            let apiOrderingName = this.fields.find(fld => fld.key === this.sortByField).api_ordering_name;
            return (this.sortDesc ? '-' : '') + apiOrderingName;
        },
        getNewList: function() {
            // call getList(), but must reset currentPage first
            // call this from any change to filters, sort, search, etc
            this.currentPage = 1;
            this.$nextTick(() => this.getList());
        },
        getList: function() {  
            this.loading = true;
            // axiosConfig['params'] is where we put search/filter params
            let filterParams = {  // grab the current search/filter values
                search: this.currentFilterValues.search || null,
                portal_access: this.currentFilterValues.portal_access,
                // if you have more filters, put them here
            };
            if (this.companyId) filterParams['company__id'] = this.companyId;  // if you passed in companyId prop, set filter for that
            let orderingParams = {
                ordering: this.getOrderingFieldString(),
            };
            let paginationParams = {
                page: this.currentPage,
                page_size: this.perPage,
            };
            let combinedParams = Object.assign({}, filterParams, orderingParams, paginationParams);
            let axiosConfig = {params: combinedParams};
            suppliosApi.contact.list(axiosConfig)
            .then(response => {
                this.totalRows = response.data.count;
                this.itemList = Array.isArray(response.data.results) ? response.data.results.map(item => {
                    return {
                        ...item,
                        portalAccessParams: getPortalAccessParams(item.portal_access),
                    }
                }) : [];
                this.$emit('list-loaded', this.totalRows);  // emit total count to parent
                // console.log("Got itemList from getList function");
                // console.log(this.itemList);
                // need to set hasAppliedFilters after we get response, not before
                // not needed anymore... you can use showFilterReset for this instead it seems... just commenting out for now
                // this.hasAppliedFilters = Object.values(filterParams).some(val => !!val);  // goes through all filter values, if any are not empty/null, returns true
            })
            .catch(error => {
                this.handleError(error, 'Error while loading itemList');
            })
            .finally(() => {
                console.log('hello from finally of getList');
                this.initialLoading = false;  // set flag to say we already loaded once
                this.loading = false;
            });
        },
        inviteToPortal: function(tuId) {
            this.loadingInvite = true;
            let inviteData = {
                detail_response: false,  // our endpoint checks for this... if false, it won't send a detailed response of the updated TU/contact
            };
            return suppliosApi.contact.inviteToPortal(tuId, inviteData)
                .then(response => {
                    // response.data will be updated detail of this TU contact if you pass detail_response: true, otherwise just a simple 'invite': 'sent'
                    // since this is a list view, just update the list, without filter reset
                    console.log(response.data);
                    this.getList();  // this will refresh the list, but not reset the filters or current page
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while performing the invite');
                    return false;
                })
                .then(status => {
                    this.loadingInvite = false;
                    return status;
                });
        },
        async startDeactivate(tuId) {
            // show modal to warn about deactivating, then if confirmed, call the real one
            const foundItem = this.itemList.find(tuObj => (tuObj.id === tuId));
            if (!foundItem) {
                return;
            }
            const tuFullName = foundItem.full_name || 'this user';
            const confirmOk = await this.$bvModal.msgBoxConfirm(`Are you sure you want to deactivate ${tuFullName}?  This is will remove them from your contact database, and prevent them from logging in (if they had access).`, {
                title: 'Please Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Deactivate',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });
            if (confirmOk) {
                await this.performDeactivate(tuId);
            }
        },
        async performDeactivate(tuId) {
            //this.loading = true;
            try {
                const { data } = await suppliosApi.contact.deactivate(tuId);
                // data is just a string message saying person was deleted
                this.$toast.success(data);
            } catch (err) {
                this.handleError(err, 'Error while deactivating this user.');
            }
            this.getList();  // this will refresh the list, but not reset the filters or current page
            // ugghh... should make getList async/await syntax....
            //this.loading = false;
        },
        isFilterEnabled: function(fieldKey) {
            // checks if a specific filter is enabled based on hideFields
            // filterName should match the field 'key' used for that field
            if (!this.hideFields || !this.hideFields.length) return true;
            return !this.hideFields.includes(fieldKey);
        },
    }
}
</script>

<style>

</style>
